import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { auth } from '../utils/api';
import toast from 'react-hot-toast';
import LocationPicker from '../components/LocationPicker';

/* ── shared wrapper ──────────────────────────────────────────────────────── */
function AuthWrap({ children }) {
  return (
    <>
      <div style={{ minHeight:'calc(100vh - var(--nav-h))', display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 20px' }}>
        {children}
      </div>
      <style>{`
        .auth-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius-xl);padding:40px;width:100%;max-width:460px}
        .auth-logo{font-size:24px;font-weight:700;margin-bottom:6px}
        .auth-title{font-size:22px;font-weight:700;margin-bottom:28px}
        .auth-foot{text-align:center;font-size:13px;color:var(--text3);margin-top:20px}
        @media(max-width:520px){.auth-card{padding:28px 20px;border-radius:var(--radius-lg)}}
      `}</style>
    </>
  );
}

/* ── Login ───────────────────────────────────────────────────────────────── */
export function LoginPage() {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [form, setForm]   = useState({ email:'', password:'' });
  const [busy, setBusy]   = useState(false);

  const set = k => e => setForm(f => ({...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await auth.login(form);
      login(res.token, res);
      toast.success('Welcome back!');
      const role = res.role;
      if (role === 'SUPERADMIN') navigate('/admin');
      else if (role === 'MODERATOR') navigate('/moderator');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setBusy(false); }
  };

  return (
    <AuthWrap>
      <div className="auth-card">
        <div className="auth-logo"><span style={{color:'var(--text)'}}>Kajer</span><span style={{color:'var(--brand)'}}>Bazar</span></div>
        <h1 className="auth-title">{t('login')}</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('email')}</label>
            <input className="form-input" type="email" required value={form.email} onChange={set('email')} autoComplete="email" />
          </div>
          <div className="form-group">
            <label className="form-label">{t('password')}</label>
            <input className="form-input" type="password" required value={form.password} onChange={set('password')} autoComplete="current-password" />
          </div>
          <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={busy}>
            {busy ? <span className="spinner" style={{width:16,height:16}}/> : t('login')}
          </button>
        </form>
        <p className="auth-foot">Don't have an account? <Link to="/register">{t('nav_register')}</Link></p>
      </div>
    </AuthWrap>
  );
}

/* ── Register ────────────────────────────────────────────────────────────── */
const ROLES = [
  { value:'SEEKER',    emoji:'🔍', label:'Job seeker',  desc:'Find jobs & publish your hire ad' },
  { value:'POSTER',    emoji:'📋', label:'Job poster',   desc:'Post jobs & hire seekers' },
  { value:'MODERATOR', emoji:'⚖️', label:'Moderator',   desc:'Review token requests (pending approval)' },
];
const MOD_FIELDS = ['JOBS','SEEK_ADS','GENERAL'];

export function RegisterPage() {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ 
    fullName:'', email:'', phone:'', password:'', role:'SEEKER', moderatorField:'GENERAL',
    gender: '', nidNumber: '', dob: '', locationId: null
  });
  const [busy, setBusy] = useState(false);
  const set = k => e => setForm(f => ({...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await auth.register(form);
      if (form.role === 'MODERATOR') {
        toast.success('Moderator account created! Awaiting super-admin approval.');
        navigate('/login');
        return;
      }
      login(res.token, res);
      toast.success('Account created! Welcome to KajerBazar 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setBusy(false); }
  };

  return (
    <AuthWrap>
      <div className="auth-card" style={{maxWidth:520}}>
        <div className="auth-logo"><span style={{color:'var(--text)'}}>Kajer</span><span style={{color:'var(--brand)'}}>Bazar</span></div>
        <h1 className="auth-title">{t('register')}</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('name')}</label>
            <input className="form-input" required value={form.fullName} onChange={set('fullName')} />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <input className="form-input" type="email" required value={form.email} onChange={set('email')} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <input className="form-input" type="tel" required value={form.phone} onChange={set('phone')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t('password')}</label>
            <input className="form-input" type="password" required minLength={6} value={form.password} onChange={set('password')} />
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-input" required value={form.gender} onChange={set('gender')}>
                <option value="">Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date of Birth</label>
              <input className="form-input" type="date" required value={form.dob} onChange={set('dob')} />
            </div>
          </div>
          
          <div className="form-group">
            <label className="form-label">National ID Number</label>
            <input className="form-input" type="text" required value={form.nidNumber} onChange={set('nidNumber')} />
          </div>

          <div className="form-group">
            <label className="form-label">Location</label>
            <LocationPicker value={form.locationId} onChange={locId => setForm(f => ({ ...f, locationId: locId }))} />
          </div>

          {/* Role selection */}
          <div className="form-group">
            <label className="form-label">I want to</label>
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {ROLES.map(r => (
                <label key={r.value} style={{
                  display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px',
                  borderRadius:'var(--radius)', cursor:'pointer',
                  border:`1.5px solid ${form.role===r.value?'var(--brand)':'var(--border)'}`,
                  background: form.role===r.value?'var(--brand-light)':'transparent',
                  transition:'all var(--transition)',
                }}>
                  <input type="radio" name="role" value={r.value} checked={form.role===r.value} onChange={set('role')} style={{accentColor:'var(--brand)'}}/>
                  <span style={{fontSize:'18px'}}>{r.emoji}</span>
                  <div>
                    <div style={{fontSize:'13px', fontWeight:600}}>{r.label}</div>
                    <div style={{fontSize:'11px', color:'var(--text3)'}}>{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Moderator field */}
          {form.role === 'MODERATOR' && (
            <div className="form-group">
              <label className="form-label">Moderation area</label>
              <select className="form-input" value={form.moderatorField} onChange={set('moderatorField')}>
                {MOD_FIELDS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          )}

          <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={busy} style={{marginTop:'8px'}}>
            {busy ? <span className="spinner" style={{width:16,height:16}}/> : t('register')}
          </button>
        </form>
        <p className="auth-foot">Already have an account? <Link to="/login">{t('login')}</Link></p>
      </div>
    </AuthWrap>
  );
}
